import { isPaidPlan } from './controllerState';
import { topicCategoryLabels } from '../../shared/topicCategoryLabels';
import { byId } from './domUtils';
import { escapeHtml, formatCompact } from './formatUtils';
import { disposeKeywordCloud, renderKeywordCloud } from './keywordCloud';
import type { Root } from './prototypeTypes';
import type { ChapterProgressOverlay } from './chapterProgressTracker';
import {
	cleanText,
	cleanTextList,
	isSafeClassToken,
	metricNumber,
	resolveKeywords,
	type ChapterRow,
	type ResumeResult,
	type TopicDetail,
} from './topicDetailPresentationUtils';

export function paintDetail(
	root: Root,
	detail: TopicDetail,
	plan: string = 'free',
	keywordOverride?: Array<{ keyword: string; coefficient: number }>,
	access: { ownerAccess?: boolean } = {},
): void {
	const title = cleanText(detail.title) ?? 'Topic not found';
	paintHeroOptionalText(root, 'topic-title', title, false);
	paintHeroOptionalText(root, 'topic-desc', cleanText(detail.subtitle), true);
	paintHeroPills(root, topicCategoryLabels(detail.category, detail.classification));
	paintPublicAnswerBlocks(root, detail);
	const metrics = detail.engagement ?? {};
	paintMetric(root, 'topic-chapters', metricNumber(metrics.chapters ?? detail.chapterCount), (value) => `${formatCompact(value)} chapters`);
	paintMetric(root, 'topic-duration', metricNumber(metrics.mediaMinutes ?? detail.audioMinutes), (value) => `${formatCompact(value)} min total`);
	syncHeroMetrics(root);
	const banner = byId(root, 'topic-banner-bg');
	const gradient = cleanText(detail.coverGradient);
	if (banner && gradient && isSafeClassToken(gradient)) banner.classList.add(gradient);
	paintTopicPromise(root, resolveKeywords(keywordOverride, detail, title));
	const premiumBlock = byId(root, 'topicPremiumUnlocks');
	if (premiumBlock) premiumBlock.style.display = access.ownerAccess === true || isPaidPlan(plan as Parameters<typeof isPaidPlan>[0]) ? 'none' : '';
}

export function paintTopicPrimaryAction(
	root: Root,
	topic: Pick<TopicDetail, 'id' | 'slug'>,
	plan: string,
	resumeResult: ResumeResult,
	initialChapterIndex: number = 1,
	optimisticProgress: ChapterProgressOverlay | null = null,
	directoryChapters: Array<{ index: number; title: string; duration?: string }> = [],
): void {
	const action = byId(root, 'topicPrimaryCta');
	const actions = byId(root, 'topic-hero-actions');
	const card = byId(root, 'topicPrimaryCtaCard') as HTMLDivElement | null;
	const slug = cleanText(topic.slug);
	if (!action || !slug) {
		if (actions) actions.hidden = true;
		return;
	}
	if (actions) actions.hidden = false;
	const startChapterIndex = Number.isInteger(initialChapterIndex) && initialChapterIndex > 0 ? initialChapterIndex : 1;
	// /api/user/progress/recent dedupes per topic and sorts by updated_at DESC,
	// so the first matching entry is always the viewer's most recently learned
	// chapter of this topic - completed or not, "Continue" resumes right there.
	const progress = optimisticProgress?.topicId === slug
		? optimisticProgress
		: resumeResult.response?.recent?.find((item) => item.topicSlug === slug || item.topicId === topic.id);
	const target = plan === 'visitor'
		? { verb: 'Start', label: 'Start Chapter', chapterIndex: startChapterIndex }
		: progress
			? { verb: 'Continue', label: 'Continue Chapter', chapterIndex: progress.chapterIndex }
			: resumeResult.loaded
				? { verb: 'Start', label: 'Start Chapter', chapterIndex: startChapterIndex }
				: null;
	const setAction = (label: string, protoAction?: string) => {
		action.classList.remove('loading');
		delete action.dataset.topicWarmActionPending;
		action.setAttribute('aria-busy', 'false');
		action.removeAttribute('aria-label');
		const copy = document.createElement('span');
		copy.className = 'topic-primary-cta-label';
		copy.textContent = label;
		const arrow = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
		arrow.setAttribute('aria-hidden', 'true');
		arrow.setAttribute('focusable', 'false');
		arrow.setAttribute('viewBox', '0 0 24 24');
		arrow.setAttribute('width', '18');
		arrow.setAttribute('height', '18');
		arrow.innerHTML = '<path d="m9 18 6-6-6-6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>';
		action.replaceChildren(copy, arrow);
		action.setAttribute('href', protoAction ? `/topics/${encodeURIComponent(slug)}/chapters/${protoAction.match(/\d+/)?.[0] ?? '1'}` : '#topicChapterList');
		if (protoAction) action.setAttribute('data-proto-click', protoAction);
		else action.removeAttribute('data-proto-click');
	};
	if (!target) {
		// A loading CTA is installed before hydration starts. This function runs
		// only after the resume read has settled, including its error fallback, so
		// an unavailable resume must resolve to the safe directory action rather
		// than leaving the Hero permanently busy.
		setAction('View chapters');
		action.style.display = '';
		return;
	}
	const protoAction = `showArticle(${target.chapterIndex})`;
	setAction(`${target.label} ${target.chapterIndex}`, protoAction);
	// Mobile (prototype round-4) replaces the pill with the Home Recent-card
	// split: the copy block (kicker + chapter title) navigates, and the
	// trailing borderless 40px play disc plays that chapter immediately.
	// R5 extends the same card to every width >=641 (R8 folded the former
	// 641-1023 tablet band into the PC hero), where the duration moves into
	// its own mono slot and the title drops the " · duration" suffix.
	const pcCta = Boolean(window.matchMedia?.('(min-width: 641px)').matches);
	if (!card || !(window.matchMedia?.('(max-width: 640px)').matches || pcCta)) {
		action.style.display = '';
		return;
	}
	const chapter = (directoryChapters.length ? directoryChapters : readSsrDirectoryChapters(root))
		.find((row) => row.index === target.chapterIndex);
	if (!chapter) {
		action.style.display = '';
		return;
	}
	const kicker = byId(root, 'topicPrimaryCtaKicker');
	const title = byId(root, 'topicPrimaryCtaTitle');
	const link = byId(root, 'topicPrimaryCtaLink');
	const play = byId(root, 'topicPrimaryCtaPlay');
	const dur = byId(root, 'topicPrimaryCtaDur');
	if (!kicker || !title || !link || !play) return;
	// Single-line CTA copy: "Ch.2: {title} · 7 min". Kicker carries the
	// chapter number only — the Start/Continue verb was dropped from the
	// kicker (PC round-8); the resume verb still leads the pill label above.
	// The title carries the chapter name + duration and ellipsizes when the
	// row overflows. A glyph would read as a second play control beside the
	// trailing play disc, so the number stays text.
	kicker.textContent = `Ch.${target.chapterIndex}:`;
	// Seed chapter titles store their own "Ch.N: " prefix. The kicker already
	// numbers the chapter, so the stored prefix is display-only noise here
	// ("Ch.1: Ch.1: Foundations") — strip it for this row only; the chapter
	// list, player and aria labels keep the stored title verbatim.
	const displayTitle = chapter.title.replace(/^Ch\.\d+:\s*/u, '');
	if (pcCta && dur) {
		dur.textContent = chapter.duration ?? '';
		dur.hidden = !chapter.duration;
		title.textContent = displayTitle;
	} else {
		if (dur) dur.hidden = true;
		title.textContent = `${displayTitle}${chapter.duration ? ` · ${chapter.duration}` : ''}`;
	}
	link.setAttribute('href', `/topics/${encodeURIComponent(slug)}/chapters/${target.chapterIndex}`);
	link.setAttribute('data-proto-click', protoAction);
	play.dataset.protoClick = `playTopicChapter(${target.chapterIndex})`;
	play.setAttribute('aria-label', `Play Chapter ${target.chapterIndex}: ${chapter.title}`);
	card.hidden = false;
	action.style.display = 'none';
}

/**
 * SSR topic pages ship the chapter rows server-side; this reader recovers
 * the directory from them so the mobile CTA card paints on the SSR-direct
 * and return-reconciler paths, which never fetch the chapter list.
 */
function readSsrDirectoryChapters(root: Root): Array<{ index: number; title: string; duration?: string }> {
	const container = byId(root, 'topicChapterItems');
	if (!container) return [];
	return [...container.querySelectorAll<HTMLElement>('.article-item')].flatMap((item) => {
		const play = item.querySelector<HTMLElement>('.article-row-play[data-proto-click]');
		const index = Number.parseInt(play?.dataset.protoClick?.match(/playTopicChapter\(\s*(\d+)/)?.[1] ?? '', 10);
		if (!Number.isInteger(index) || index <= 0) return [];
		const title = item.querySelector<HTMLElement>('.article-title')?.textContent?.trim() ?? '';
		const duration = item.querySelector<HTMLElement>('.article-duration')?.textContent?.trim() || undefined;
		return title ? [{ index, title, duration }] : [];
	});
}

export function paintPremiumChips(root: Root, chapters: ChapterRow[], plan: string = 'free', access: { ownerAccess?: boolean } = {}): void {
	if (!chapters.length) return;
	const hasAuthoritativeDirectoryAccess = chapters.some((chapter) => typeof chapter.access?.allowed === 'boolean');
	const isPaid = hasAuthoritativeDirectoryAccess
		? chapters.every((chapter) => chapter.access?.allowed === true)
		: access.ownerAccess === true || isPaidPlan(plan as Parameters<typeof isPaidPlan>[0]);
	const premiumBlock = byId(root, 'topicPremiumUnlocks');
	if (premiumBlock) premiumBlock.style.display = isPaid ? 'none' : '';
	const freeCount = chapters.filter((chapter) => chapter.isFreePreview).length;
	const free = freeCount || Math.min(3, chapters.length);
	const chips = byId(root, 'topicPremiumPills');
	if (chips) {
		if (isPaid) chips.replaceChildren(buildChip('All chapters unlocked', true));
		else {
			// Range chips mirror the SSR paywall band: one Free run and one
			// Premium run with their real extents, so the badges can never read
			// as if the Topic ends at the last rendered chip.
			const freeIndexes = freeCount
				? chapters.filter((chapter) => chapter.isFreePreview).map((chapter) => chapter.index)
				: Array.from({ length: free }, (_, offset) => offset + 1);
			const freeSet = new Set(freeIndexes);
			const lockedIndexes = chapters.map((chapter) => chapter.index).filter((index) => !freeSet.has(index));
			chips.replaceChildren(...buildRangeChips(freeIndexes, 'Free', true), ...buildRangeChips(lockedIndexes, 'Premium', false));
		}
	}
	set(root, 'topicPremiumHeadline', isPaid ? 'All chapters unlocked · Audio + Video' : `Preview every chapter · Unlock full text, audio, and video from chapter ${free + 1}`);
}

/** Collapses a contiguous index run into one chip; non-contiguous runs fall back to the capped list. */
function buildRangeChips(indexes: number[], tier: 'Free' | 'Premium', available: boolean): HTMLElement[] {
	if (!indexes.length) return [];
	const sorted = [...new Set(indexes)].sort((a, b) => a - b);
	const first = sorted[0];
	const last = sorted[sorted.length - 1];
	if (last - first + 1 === sorted.length) {
		return [buildChip(first === last ? `Ch.${first} ${tier}` : `Ch.${first}-${last} ${tier}`, available)];
	}
	return [
		...sorted.slice(0, 3).map((index) => buildChip(`Ch.${index} ${tier}`, available)),
		...(sorted.length > 3 ? [buildChip(`Ch.${sorted[3]}+`, available)] : []),
	];
}

function paintPublicAnswerBlocks(root: Root, detail: TopicDetail): void {
	const summaryText = cleanText(detail.summary);
	const summaryBlock = byId(root, 'topic-summary-block');
	const summary = byId(root, 'topic-summary');
	if (summaryBlock && summary) {
		summary.textContent = summaryText ?? '';
		summaryBlock.hidden = !summaryText;
		(summaryBlock as HTMLDetailsElement).open = true;
	}
	const takeawaysBlock = byId(root, 'topic-takeaways-block');
	const takeaways = cleanTextList(detail.keyTakeaways ?? detail.outcomes);
	if (takeawaysBlock) {
		takeawaysBlock.hidden = takeaways.length === 0;
		(takeawaysBlock as HTMLDetailsElement).open = false;
		takeawaysBlock.innerHTML = `<summary>Learning outcomes</summary><ul>${takeaways.map((item) => `<li>${escapeHtml(item)}</li>`).join('')}</ul>`;
	}
	const keywords = resolveKeywords(undefined, detail, cleanText(detail.title) ?? '');
	paintMobileKeywords(root, keywords);
	syncInformationBand(root, Boolean(summaryText), takeaways.length > 0, keywords.length > 0);
	const faqBlock = byId(root, 'topic-faq-block');
	if (faqBlock) {
		const faqs = (Array.isArray(detail.faqs) ? detail.faqs : []).flatMap((faq) => {
			const question = cleanText(faq?.question);
			const answer = cleanText(faq?.answer);
			return question && answer ? [{ question, answer }] : [];
		});
		faqBlock.innerHTML = faqs.length ? `<section class="faq" aria-labelledby="topic-faq-title"><h2 id="topic-faq-title">FAQ</h2>${faqs.map((faq) => `<details><summary>${escapeHtml(faq.question)}</summary><p>${escapeHtml(faq.answer)}</p></details>`).join('')}</section>` : '';
	}
}

function paintTopicPromise(root: Root, keywords: Array<{ keyword: string; coefficient: number }>): void {
	const card = byId(root, 'topic-outcomes-block');
	const cloud = byId(root, 'topic-keyword-cloud');
	if (!card || !cloud) return;
	if (!keywords.length) {
		disposeKeywordCloud(cloud);
		cloud.replaceChildren();
		cloud.classList.remove('kw-cloud-3d', 'kw-cloud--skeleton', 'topic-keyword-cloud--static');
		card.hidden = true;
		return;
	}
	card.hidden = false;
	renderKeywordCloud(cloud, keywords);
}

function paintMobileKeywords(root: Root, keywords: Array<{ keyword: string; coefficient: number }>): void {
	const block = byId(root, 'topic-keywords-mobile-block') as HTMLDetailsElement | null;
	const cloud = byId(root, 'topic-keyword-cloud-mobile');
	if (!block || !cloud) return;
	block.hidden = keywords.length === 0;
	block.open = false;
	cloud.replaceChildren(...keywords.map((item) => {
		const chip = document.createElement('span');
		chip.className = 'topic-keyword-static';
		chip.style.setProperty('--kw-weight', String(Math.max(0, Math.min(1, item.coefficient / 10))));
		chip.textContent = item.keyword;
		return chip;
	}));
}

function syncInformationBand(root: Root, hasSummary: boolean, hasTakeaways: boolean, hasKeywords = false): void {
	const band = byId(root, 'topic-info-band');
	if (!band) return;
	const hasContent = hasSummary || hasTakeaways || hasKeywords;
	band.hidden = !hasContent;
	band.classList.remove('topic-info-band--single');
}

function buildChip(label: string, free: boolean): HTMLElement {
	const chip = document.createElement('div');
	chip.className = 'pp-chip' + (free ? '' : ' locked');
	chip.style.cssText = free ? 'border-color:var(--success);background:var(--success-light);' : '';
	chip.innerHTML = free
		? '<svg aria-hidden="true" focusable="false" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--success)" stroke-width="2.5"><polyline points="20 6 9 17 4 12"></polyline></svg>' + escapeHtml(label)
		: '<svg aria-hidden="true" focusable="false" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="11" width="18" height="11" rx="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>' + escapeHtml(label);
	return chip;
}

function set(root: Root, id: string, value: string): void {
	const element = byId(root, id);
	if (element) element.textContent = value;
}

const metricIcons: Record<string, string> = {
	'topic-chapters': '<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="metric-icon"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path></svg>',
	'topic-duration': '<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="metric-icon"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>',
};

function paintMetric(root: Root, id: string, value: number | undefined, label: (value: number) => string): void {
	const element = byId(root, id);
	if (!element) return;
	if (value === undefined) {
		element.hidden = true;
		element.replaceChildren();
		delete element.dataset.metricValue;
		return;
	}
	const text = label(value);
	element.hidden = false;
	element.classList.remove('tdb-metric--loading');
	if (element.dataset.metricValue === text) return;
	element.dataset.metricValue = text;
	element.innerHTML = (metricIcons[id] ?? '') + '<span>' + escapeHtml(id === 'topic-duration' ? text.replace(/ total$/u, '') : text)
		+ (id === 'topic-duration' && text.endsWith(' total') ? '<span class="topic-metric-total"> total</span>' : '') + '</span>';
}

function syncHeroMetrics(root: Root): void {
	const metrics = byId(root, 'topic-hero-metrics');
	if (metrics) metrics.hidden = ![...metrics.children].some((metric) => !(metric as HTMLElement).hidden);
}

function paintHeroOptionalText(root: Root, id: string, value: string | undefined, optional: boolean): void {
	const element = byId(root, id);
	if (!element) return;
	element.classList.remove('topic-hero-loading');
	element.textContent = value ?? '';
	element.hidden = optional && !value;
}

function paintHeroPills(root: Root, categories: string[]): void {
	const pills = byId(root, 'topic-hero-pills');
	if (!pills) return;
	pills.replaceChildren(...categories.map((category, index) => {
		const pill = document.createElement('span');
		pill.className = `pill pill-category${index === 0 ? ' topic-cat-pill--primary' : ''}`;
		if (index === 0) {
			pill.id = 'topic-cat-pill';
			pill.dataset.bind = 'topic.category';
		}
		pill.textContent = category;
		return pill;
	}));
	pills.hidden = categories.length === 0;
}
